/**
 * Combine Lots - data loader + per-BBL cache.
 *
 * Phase 1 caches only the click-time tile geometry on each BBL (used by
 * the map overlay). Phase 2 also needs the lot's address, owner, SF
 * totals, etc. - fetched from the server endpoint
 * `GET /lots/combined_lots_data?bbls=...` (LotsController#combined_lots_data,
 * which calls DataLayers::LotData.combined_lots_data).
 *
 * To avoid refetching everything on each toggle, we keep a singleton
 * Map<bbl, lotRow> as a cache. On each store change:
 *
 *   1. newBbls = currentSelection - cachedBbls
 *   2. If newBbls.length, fetch JUST those and merge into the cache.
 *   3. Build the grid-facing row array from the cache (filtered to
 *      currentSelection, preserving the store's order).
 *
 * The cache survives until page reload (matches PsCombinedLots's
 * in-memory-only persistence). Lots removed from the selection stay in
 * the cache so re-adding them is instant.
 *
 * Exposed as `window.PsCombinedLotsDataLoader`.
 *
 * Globals consumed:
 *   - `window.PsCombinedLots`           (selection store, phase 1)
 *   - `window.log_ajax_error`           (global ajax error reporter +
 *                                        opt-in user-facing modal)
 */

const cache = new Map();      // bbl(string) -> lot row
const listeners = new Set();
let lastSelectionKey = '';    // simple change-detection key

let inflight = null;          // jqXHR guarding concurrent fetches

function selectionKey(bbls) {
  // Order-sensitive key - if the selection order changes (e.g. future
  // reordering), we want subscribers to re-fire. Cheap O(n) join.
  return (bbls || []).join('|');
}

function notify() {
  const rows = currentRows();
  listeners.forEach((fn) => {
    try { fn(rows); } catch (err) { console.error('[combined-lots-data] listener error', err); }
  });
}

/**
 * Build a placeholder row for a BBL whose server fetch hasn't landed
 * yet. Pulls whatever fields are available from the click-time
 * Mapbox feature properties (cached on PsCombinedLots), so the row
 * shows up in the grid immediately with address + lot # + building
 * class populated. Server-only fields (owner, SF totals,
 * underbuilt_color) are left null; the grid's formatters check
 * `_loading: true` and render "Loading..." placeholders for those.
 */
function placeholderRowFor(bbl) {
  const bblStr = String(bbl);
  const props = (window.PsCombinedLots && window.PsCombinedLots.getProperties)
    ? (window.PsCombinedLots.getProperties(bbl) || {})
    : {};

  // Parse lot number from BBL (last 4 digits of the 10-digit BBL).
  // Falls back to null if the BBL string is short/malformed.
  let lotNum = null;
  if (bblStr.length >= 10) {
    const parsed = parseInt(bblStr.slice(6), 10);
    if (!Number.isNaN(parsed)) { lotNum = parsed; }
  }

  // Bld Class / Yr cell: prop_type's first 2 chars + year if known.
  // Mirrors what the map hover shows.
  let buildingClass = null;
  let classYear = null;
  if (props.prop_type) {
    buildingClass = String(props.prop_type).slice(0, 2);
    classYear = props.year ? `${buildingClass} / ${props.year}` : buildingClass;
  }

  // CSS class name for the colored chip in the Class column. Mirrors
  // `Lot#land_use_class` ("landuse_<N>" or "landuse_other"). The
  // Mapbox feature carries the numeric land_use; if it's missing,
  // fall back to the neutral grey "other" chip.
  const landUseClass = props.land_use
    ? `landuse_${props.land_use}`
    : 'landuse_other';

  // Lot area is carried on the Mapbox tile feature (already used
  // elsewhere for zoom-fit decisions), so we can show it in the
  // placeholder row without waiting for the server.
  const lotArea = (props.area != null && props.area !== '')
    ? Number(props.area)
    : null;

  return {
    bbl: bblStr,
    lot: lotNum,
    address: props.address || null,
    class_year: classYear,
    building_class: buildingClass,
    land_use_class: landUseClass,
    // Zoning districts are server-only - not carried on the tile
    // feature, so placeholder shows the loading dots until the
    // fetch resolves.
    zoning_districts: null,
    // Lot dimensions - area comes from the tile feature so it shows
    // immediately; frontage/depth are server-only.
    lot_area: lotArea,
    lot_frontage: null,
    lot_depth: null,
    owner: null,
    underbuilt_color: null,
    // SF + FAR (all server-only - render as "..." placeholders).
    built_sf_total: null,
    permitted_sf_total: null,
    remaining_sf_total: null,
    built_sf_res: null,
    permitted_sf_res: null,
    remaining_sf_res: null,
    built_far_total: null,
    permitted_far_total: null,
    remaining_far_total: null,
    built_far_res: null,
    permitted_far_res: null,
    remaining_far_res: null,
    // Unit + building geometry. Tile feature carries `built_h` so
    // height shows immediately; res_units / rs_units / stories are
    // server-only.
    res_units: null,
    rs_units: null,
    built_height: (props.built_h != null && props.built_h !== '') ? Number(props.built_h) : null,
    stories: null,
    _loading: true,
  };
}

/**
 * Returns the rows for the current selection, in selection order.
 * For BBLs not yet in the server-row cache, returns a click-time
 * placeholder so the grid can render the row immediately and the
 * user sees their selection reflected without waiting for the fetch.
 */
function currentRows() {
  const selected = (window.PsCombinedLots && window.PsCombinedLots.getSelected()) || [];
  const rows = [];
  selected.forEach((bbl) => {
    const row = cache.get(String(bbl));
    rows.push(row || placeholderRowFor(bbl));
  });
  return rows;
}

/**
 * Fetch any BBLs not yet in the cache and merge them in. Idempotent and
 * safe to call concurrently - overlapping calls share the same promise.
 *
 * Returns a Promise that resolves once the cache reflects the CURRENT
 * selection (or as much of it as the server returned).
 */
function ensureLoaded() {
  const selected = (window.PsCombinedLots && window.PsCombinedLots.getSelected()) || [];
  const missing = selected.filter((bbl) => !cache.has(String(bbl)));

  if (missing.length === 0) {
    return inflight || Promise.resolve();
  }

  // If a fetch is already in flight, wait for it (success OR failure)
  // then re-check - the in-flight fetch may already cover our missing
  // BBLs (or new ones may have been added since it started). Wrap in
  // a native Promise so a rejected jqXHR doesn't propagate up as an
  // unhandled rejection.
  if (inflight) {
    return new Promise((resolve) => {
      inflight.always(() => resolve(ensureLoaded()));
    });
  }

  const url = `/lots/combined_lots_data?bbls=${encodeURIComponent(missing.join(','))}`;
  const requestedBbls = missing.slice();

  // jQuery $.ajax for consistency with the rest of the app and to lean
  // on the global log_ajax_error reporter (auto-POSTs to /api/ajax_error
  // for admin email + DynamoDB capture, and surfaces the global modal
  // when showToUser is true). showToUser is true here because the user
  // is staring at placeholder loading dots in the grid and we want them
  // to know the fetch failed rather than waiting indefinitely.
  const ajaxPromise = $.ajax({
    url: url,
    method: 'GET',
    dataType: 'json',
    headers: { 'Accept': 'application/json' },
  });
  inflight = ajaxPromise;

  ajaxPromise.done((payload) => {
    const lots = (payload && payload.lots) || [];
    const returnedBbls = new Set();
    lots.forEach((row) => {
      if (row && row.bbl != null) {
        cache.set(String(row.bbl), row);
        returnedBbls.add(String(row.bbl));
        // Option D (GH #270): swap the click-time tile-clipped
        // geometry in PsCombinedLots for the server-side canonical
        // full-lot polygon. The map overlay subscriber will pick up
        // the change via PsCombinedLots's own notify and re-render
        // the dot pattern on the full lot. No-op if the user removed
        // the BBL between fetch start and resolve.
        if (row.geometry && window.PsCombinedLots && window.PsCombinedLots.updateGeometry) {
          window.PsCombinedLots.updateGeometry(row.bbl, row.geometry);
        }
      }
    });

    // Detect server-side row drops. The server is supposed to return
    // one row per requested BBL; if any are missing it means a
    // row-build rescue swallowed the failure and the user's grid
    // would stay on placeholder dots indefinitely. Surface as a
    // js_error (no jqXHR) and show the modal.
    const missingBbls = requestedBbls.filter((bbl) => !returnedBbls.has(String(bbl)));
    if (missingBbls.length > 0 && typeof window.log_js_error === 'function') {
      const msg = `combined_lots_data: server returned ${lots.length} rows for ${requestedBbls.length} requested BBLs`;
      const details = [
        `requested_bbls: ${requestedBbls.join(', ')}`,
        `returned_bbls:  ${Array.from(returnedBbls).join(', ')}`,
        `missing_bbls:   ${missingBbls.join(', ')}`,
        `url:            ${url}`,
      ].join('\n');
      window.log_js_error(msg, details, {
        showToUser: true,
        userMessage: "Couldn't load all combined-lots data. Our team has been notified — please try again or remove the affected lots.",
      });
    }
  });

  ajaxPromise.fail((jqXHR) => {
    if (typeof window.log_ajax_error === 'function') {
      window.log_ajax_error(jqXHR, {
        showToUser: true,
        userMessage: "Couldn't load combined-lots data. Our team has been notified — please try again.",
      });
    }
  });

  ajaxPromise.always(() => {
    inflight = null;
    notify();
  });

  return inflight;
}

/**
 * Subscribe to data refreshes. Called whenever `ensureLoaded` finishes
 * (selection now has its server-side rows) or whenever the selection
 * shrinks (no fetch needed, but row set changed). Receives the current
 * `currentRows()` array.
 */
function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function getRows() { return currentRows(); }

function clearCache() { cache.clear(); }

// Wire to the selection store so any change kicks off a fetch (if
// needed) and then re-notifies subscribers. Runs once at module load.
function attachToStore() {
  if (!(typeof window !== 'undefined' && window.PsCombinedLots)) {
    // PsCombinedLots imports run first in application.js, so this should
    // never happen - but if it does, fail loud, not silent.
    console.warn('[combined-lots-data] PsCombinedLots not available at attach time');
    return;
  }

  window.PsCombinedLots.subscribe((bbls) => {
    const key = selectionKey(bbls);
    if (key === lastSelectionKey) { return; }
    lastSelectionKey = key;

    if (bbls.length === 0) {
      // Selection empty - nothing to fetch. Just notify so the grid can
      // tear down.
      notify();
      return;
    }

    // ALWAYS notify immediately on selection change so the grid paints
    // placeholder rows for any BBLs not yet in cache. Then kick off the
    // fetch - a second notify fires when it resolves, replacing the
    // placeholders with real rows. Without this immediate notify, the
    // grid would be blank for the duration of the server round-trip
    // (~1s under normal conditions), which made the selection feel
    // disconnected from what was painted on the map.
    notify();

    // Re-apply cached server geometry to PsCombinedLots for any BBLs
    // we already have a server row for. Why this matters: when a lot
    // is removed from PsCombinedLots, its cached geometry is dropped
    // from PsCombinedLots's `entries` map, but the data loader's
    // `cache` map keeps the full server row (including geometry). On
    // RE-ADD of the same BBL, PsCombinedLots only gets the click-time
    // tile-clipped geometry from the new feature; without this loop,
    // `ensureLoaded` skips the fetch (cache hit), `updateGeometry`
    // never fires, and the dot pattern only paints on the visible-
    // tile portion of the lot. See GH #270.
    bbls.forEach((bbl) => {
      const row = cache.get(String(bbl));
      if (row && row.geometry && window.PsCombinedLots && window.PsCombinedLots.updateGeometry) {
        window.PsCombinedLots.updateGeometry(bbl, row.geometry);
      }
    });

    const missing = bbls.filter((bbl) => !cache.has(String(bbl)));
    if (missing.length > 0) {
      ensureLoaded();
    }
  });
}

attachToStore();

const api = { ensureLoaded, getRows, subscribe, clearCache };

if (typeof window !== 'undefined') {
  window.PsCombinedLotsDataLoader = api;
}

export default api;
